import { useEffect, useState } from "react";
import { upsertAdsSocialPost, uploadManualAdPostImages, type AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { useAdsAssistantManualPostMediaUpload } from "@/components/ads-assistant/useAdsAssistantManualPostMediaUpload";
import { usePruneSlotFillJsonForTemplate } from "@/components/ads-assistant/usePruneSlotFillJsonForTemplate";
import { useTemplateArchetypeTagInherit } from "@/components/ads-assistant/useTemplateArchetypeTagInherit";
import { platformOptionsFromOrgChecklistConfig, type AdsPlatformSelectOption } from "@/lib/orgAdsPlatformOptions";
import { requireSupabase, supabase } from "@/lib/supabase";

/** Manual ingest form state, validation, and submit. Composed by useAdsAssistantAggregation. */
export function useAdsAssistantAggregationManualForm(args: {
  scopedOrgId: string | null;
  canQuality: boolean;
  /** True when the rep may override `published_at` (trainer+); sales role hides it entirely. */
  canSeePublishedAtOverride: boolean;
  templates: AdsAssistantTemplateRow[];
  /** When set, stamped on new posts so location index filters include the row. */
  ingestLocationId?: string | null;
  onSaved: () => void;
  setError: (e: string | null) => void;
  setSaveMsg: (m: string | null) => void;
}) {
  const { scopedOrgId, canQuality, canSeePublishedAtOverride, templates, ingestLocationId, onSaved, setError, setSaveMsg } =
    args;

  const fresh = useFreshAchievementsOptional();

  const [platformOptions, setPlatformOptions] = useState<AdsPlatformSelectOption[]>(() =>
    platformOptionsFromOrgChecklistConfig(null)
  );
  const [platform, setPlatform] = useState("facebook");
  const [externalId, setExternalId] = useState("");
  const [publishedAt, setPublishedAt] = useState("");
  const [publishOverrideOpen, setPublishOverrideOpen] = useState(false);
  const [body, setBody] = useState("");
  const [creativeDescription, setCreativeDescription] = useState("");
  const [platformCta, setPlatformCta] = useState("");
  const [qualityRating, setQualityRating] = useState<"" | "good" | "bad">("");
  const [qualityNote, setQualityNote] = useState("");
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());
  const [selectedPostTags, setSelectedPostTags] = useState<Set<string>>(new Set());
  const [templateId, setTemplateId] = useState("");
  const [slotFillJsonText, setSlotFillJsonText] = useState("");
  const [promotedExemplar, setPromotedExemplar] = useState(false);
  const [saving, setSaving] = useState(false);

  const { manualPostMediaFiles, setManualPostMediaFiles, clearManualPostMediaFiles } =
    useAdsAssistantManualPostMediaUpload();

  useEffect(() => {
    if (!scopedOrgId || !supabase) {
      setPlatformOptions(platformOptionsFromOrgChecklistConfig(null));
      return;
    }
    const sb = requireSupabase();
    void (async () => {
      const { data, error: orgErr } = await sb
        .from("organizations")
        .select("sales_daily_checklist_config")
        .eq("id", scopedOrgId)
        .maybeSingle();
      if (orgErr) {
        setPlatformOptions(platformOptionsFromOrgChecklistConfig(null));
        return;
      }
      const raw = (data as { sales_daily_checklist_config?: unknown } | null)?.sales_daily_checklist_config;
      setPlatformOptions(platformOptionsFromOrgChecklistConfig(raw));
    })();
  }, [scopedOrgId]);

  useEffect(() => {
    const keys = new Set(platformOptions.map((o) => o.key));
    if (!keys.has(platform) && platformOptions.length) {
      setPlatform(platformOptions[0]!.key);
    }
  }, [platformOptions, platform]);

  useEffect(() => {
    setPlatformCta("");
  }, [platform]);

  usePruneSlotFillJsonForTemplate(templateId, templates, setSlotFillJsonText);

  useTemplateArchetypeTagInherit({ templateId, templates, selectedPostTags, setSelectedPostTags });

  function toggleCat(id: string) {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function togglePostTag(id: string) {
    setSelectedPostTags((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 5) next.add(id);
      return next;
    });
  }

  async function onManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!scopedOrgId) return;
    const ext =
      externalId.trim() ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? `manual-${crypto.randomUUID()}`
        : `manual-${Date.now()}`);
    setSaving(true);
    setError(null);

    const payload: Parameters<typeof upsertAdsSocialPost>[0] = {
      platform,
      external_post_id: ext,
      text_content: body,
      source: "manual_seed",
      category_ids: [...selectedCats],
      template_id: templateId || undefined,
    };

    // Sales role never sends `published_at` — the server stamps logged_at=now()
    // and mirrors published_at to it. Trainer+ may override via the collapsed
    // "Override publish time" details, which only fires when they typed a value.
    if (canSeePublishedAtOverride && publishOverrideOpen && publishedAt.trim()) {
      const parsed = new Date(publishedAt);
      if (!Number.isNaN(parsed.getTime())) {
        payload.published_at = parsed.toISOString();
      }
    }
    if (creativeDescription.trim()) payload.creative_description = creativeDescription.trim();
    const cta = platformCta.trim().toLowerCase();
    if (cta) payload.platform_cta = cta;
    if (canQuality && qualityRating) {
      payload.quality_rating = qualityRating;
      if (qualityNote.trim()) payload.quality_note = qualityNote.trim();
    }

    const slotTrim = slotFillJsonText.trim();
    if (slotTrim) {
      try {
        const parsed = JSON.parse(slotTrim) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          setSaving(false);
          setError("Slot fills must be a JSON object, e.g. {\"hook\":\"...\",\"cta\":\"...\"}");
          return;
        }
        payload.slot_fill_json = parsed as Record<string, unknown>;
      } catch {
        setSaving(false);
        setError("Could not parse slot JSON — check braces and quotes.");
        return;
      }
    }

    if (canQuality && promotedExemplar) {
      payload.promoted_exemplar = true;
    }

    const tagIds = [...selectedPostTags];
    if (tagIds.length) payload.tag_ids = tagIds;

    if (ingestLocationId?.trim()) payload.location_id = ingestLocationId.trim();

    const r = await upsertAdsSocialPost(payload);
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    if (r.data.fresh_achievements?.length && fresh) {
      fresh.publish(r.data.fresh_achievements);
    }

    const postId = r.data.post.id;
    const filesToUpload = manualPostMediaFiles;
    if (filesToUpload.length) {
      const mediaErr = await uploadManualAdPostImages(postId, filesToUpload);
      if (mediaErr) {
        setError(`Post saved, but images failed: ${mediaErr}`);
        setSaveMsg("Post saved and analyzed (stub pass + policies).");
        onSaved();
        return;
      }
      clearManualPostMediaFiles();
    }

    setSaveMsg("Post saved and analyzed (stub pass + policies).");
    setBody("");
    setExternalId("");
    setCreativeDescription("");
    setPlatformCta("");
    setQualityRating("");
    setQualityNote("");
    setSlotFillJsonText("");
    setPromotedExemplar(false);
    setSelectedPostTags(new Set());
    setSelectedCats(new Set());
    setPublishedAt("");
    setPublishOverrideOpen(false);
    onSaved();
  }

  function resetManualIngestForm() {
    setBody("");
    setExternalId("");
    setCreativeDescription("");
    setPlatformCta("");
    setQualityRating("");
    setQualityNote("");
    setSlotFillJsonText("");
    setPromotedExemplar(false);
    setSelectedPostTags(new Set());
    setSelectedCats(new Set());
    setPublishedAt("");
    setPublishOverrideOpen(false);
    setTemplateId("");
    clearManualPostMediaFiles();
    setError(null);
  }

  return {
    platformOptions,
    platform,
    setPlatform,
    externalId,
    setExternalId,
    publishedAt,
    setPublishedAt,
    publishOverrideOpen,
    setPublishOverrideOpen,
    canSeePublishedAtOverride,
    body,
    setBody,
    creativeDescription,
    setCreativeDescription,
    platformCta,
    setPlatformCta,
    qualityRating,
    setQualityRating,
    qualityNote,
    setQualityNote,
    templateId,
    setTemplateId,
    slotFillJsonText,
    setSlotFillJsonText,
    promotedExemplar,
    setPromotedExemplar,
    selectedCats,
    selectedPostTags,
    saving,
    toggleCat,
    togglePostTag,
    onManualSubmit,
    resetManualIngestForm,
    manualPostMediaFiles,
    setManualPostMediaFiles,
  };
}
